import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { getAccount } from '@app/reducers/account/account-service.selectors';
import { ClockLocationService } from '@app/reducers/orm/clock-location/clock-location.service';
import { DepartmentVariationApi } from '@app/reducers/orm/department-variation/department-variation.api';
import {
  DEFAULT_SETTINGS_VARIATION_ID,
  DepartmentVariationSection,
  DepartmentVariationSettingModel,
} from '@app/reducers/orm/department-variation/department-variation.model';
import {
  getPermissionGroups,
  PermissionGroupService,
} from '@app/reducers/orm/permission-group/permission-group.service';
import { getPermissions, PermissionService } from '@app/reducers/orm/permission/permission.service';
import { TrackingService } from '@app/services/tracking.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { MapMarker, MapPlace, MapsComponent } from '@sb/maps';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { DIALOG_DATA, DialogActivationComponent, DialogRef } from '@sb/ui/dialog';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { BehaviorSubject, combineLatest, filter, finalize, map, of, switchMap, take } from 'rxjs';

import { AutoApproveTimesheetHoursOption, TimeTrackingDialogStep, TimeTrackingMethod } from '../chapter.model';
import { OnboardingChapterModule } from '../components/chapter.module';
import { TimeTrackingAutoApproveHoursComponent } from './steps/time-tracking-auto-approve-hours/time-tracking-auto-approve-hours.component';
import { TimeTrackingAutoApproveScheduleComponent } from './steps/time-tracking-auto-approve-schedule/time-tracking-auto-approve-schedule.component';
import { TimeTrackingClockingLocationComponent } from './steps/time-tracking-clocking-location/time-tracking-clocking-location.component';
import { TimeTrackingTrackingMethodComponent } from './steps/time-tracking-tracking-method/time-tracking-tracking-method.component';
import { OnboardingTimeTrackingService } from './time-tracking.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'time-tracking-dialog',
  imports: [
    DialogActivationComponent,
    TranslatePipe,
    NgStyle,
    OnboardingChapterModule,
    TimeTrackingTrackingMethodComponent,
    TimeTrackingAutoApproveHoursComponent,
    TimeTrackingAutoApproveScheduleComponent,
    TimeTrackingClockingLocationComponent,
    MapsComponent,
  ],
  providers: [PlanCheckPipe],
  templateUrl: './time-tracking-dialog.component.html',
})
export class TimeTrackingDialogComponent {
  eventData = inject(DIALOG_DATA);
  private dialogRef = inject(DialogRef);
  private departmentVariationAPI = inject(DepartmentVariationApi);
  private clockLocationService = inject(ClockLocationService);
  private trackingService = inject(TrackingService);
  private permissionGroupService = inject(PermissionGroupService);
  private permissionService = inject(PermissionService);
  private store = inject<Store<AppState>>(Store);
  private planCheck = inject(PlanCheckPipe);
  private timeTrackingService = inject(OnboardingTimeTrackingService);
  private featureService = inject(FeatureService);

  TimeTrackingDialogStep = TimeTrackingDialogStep;
  step = TimeTrackingDialogStep.TEASER;
  steps = 5;

  selectedTrackingMethod: TimeTrackingMethod;
  selectedAutoApproveHoursOption: AutoApproveTimesheetHoursOption;
  selectedAutoApproveScheduleOption: AutoApproveTimesheetHoursOption;
  clockingPlace: MapPlace;
  selectedLocationMarker: MapMarker;

  nextButtonLoading = false;
  skipButtonLoading = false;

  private timeTrackingGeneralSettings: Partial<DepartmentVariationSettingModel>;
  private timeTrackingClockingSettings: Partial<DepartmentVariationSettingModel>;

  private settingsLoaded$ = new BehaviorSubject<boolean>(false);

  private eventTracked = false;

  constructor() {
    void combineLatest([
      this.getDefaultVariationSettings(DepartmentVariationSection.TIMETRACKING_GENERAL),
      this.getDefaultVariationSettings(DepartmentVariationSection.TIMETRACKING_CLOCKING),
    ])
      .pipe(take(1))
      .subscribe(([generalSettings, clockingSettings]) => {
        this.timeTrackingGeneralSettings = generalSettings;
        this.timeTrackingClockingSettings = clockingSettings;
        this.settingsLoaded$.next(true);
      });
  }

  goToStep(step: TimeTrackingDialogStep) {
    if (this.step === TimeTrackingDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Time Tracking Started', this.eventData);
      this.eventTracked = true;
    }
    this.step = step;
  }

  goBack() {
    let newStep;

    switch (this.step) {
      case TimeTrackingDialogStep.AUTO_APPROVE_HOURS:
        newStep = TimeTrackingDialogStep.TRACKING_METHOD;
        break;
      case TimeTrackingDialogStep.CLOCKING_LOCATION:
        newStep = TimeTrackingDialogStep.AUTO_APPROVE_HOURS;
        break;
      case TimeTrackingDialogStep.AUTO_APPROVE_SCHEDULE:
        newStep = TimeTrackingDialogStep.TRACKING_METHOD;
        break;
      default:
        newStep = this.step - 1;
        break;
    }

    this.step = newStep;
  }

  onTrackingMethodChange(method: TimeTrackingMethod) {
    this.selectedTrackingMethod = method;

    switch (this.selectedTrackingMethod) {
      case TimeTrackingMethod.SELF_CLOCKING:
        this.goToStep(TimeTrackingDialogStep.TRACKING_ACCURACY);
        break;
      case TimeTrackingMethod.COPY_FROM_SCHEDULE:
        this.goToStep(TimeTrackingDialogStep.AUTO_APPROVE_SCHEDULE);
        break;
      case TimeTrackingMethod.MANUAL:
        this.savePreferencesAndClose();
        break;
    }
  }

  onAutoApproveHoursChange(option: AutoApproveTimesheetHoursOption) {
    this.selectedAutoApproveHoursOption = option;

    const hasAtLeastPremium = this.planCheck.transform(PlanType.PREMIUM);

    if (hasAtLeastPremium && this.selectedTrackingMethod === TimeTrackingMethod.SELF_CLOCKING) {
      this.goToStep(TimeTrackingDialogStep.CLOCKING_LOCATION);
    } else {
      this.savePreferencesAndClose();
    }
  }

  onAutoApproveScheduleChange(option: AutoApproveTimesheetHoursOption) {
    this.selectedAutoApproveScheduleOption = option;

    this.savePreferencesAndClose();
  }

  onLocationChange(place: MapPlace) {
    this.clockingPlace = place;
    this.selectedLocationMarker = {
      id: place.name || 'main-location',
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
  }

  savePreferencesAndClose(skipLastStep = false) {
    this.nextButtonLoading = !skipLastStep;
    this.skipButtonLoading = skipLastStep;

    if (this.featureService.isEnabled(featureFlag.TMP_TIME_TRACKING_ENDPOINT)) {
      void this.timeTrackingService
        .savePreferences(this.getTimeTrackingPreferences(skipLastStep))
        .pipe(
          finalize(() => {
            this.nextButtonLoading = false;
            this.skipButtonLoading = false;
          }),
        )
        .subscribe(() => this.dialogRef.close({ action: skipLastStep ? 'secondary' : 'primary' }));
    } else {
      void this.settingsLoaded$
        .pipe(
          filter((loaded) => loaded),
          switchMap(() => {
            const requests$ = [
              this.saveTimeTrackingGeneralSettings().pipe(switchMap(() => this.saveTimeTrackingClockingSettings())),
              this.savePermissions(),
            ];

            if (!skipLastStep && this.clockingPlace) {
              requests$.push(
                this.clockLocationService.add({
                  name: this.clockingPlace.name,
                  latitude: this.selectedLocationMarker.lat,
                  longitude: this.selectedLocationMarker.lng,
                  radius: 50,
                }),
              );
            }

            return combineLatest(requests$);
          }),
          take(1),
          finalize(() => {
            this.nextButtonLoading = false;
            this.skipButtonLoading = false;
          }),
        )
        .subscribe(() => this.dialogRef.close({ action: skipLastStep ? 'secondary' : 'primary' }));
    }
  }

  private getTimeTrackingPreferences(skipLastStep = false) {
    let clockLocation;

    if (!skipLastStep && this.clockingPlace) {
      clockLocation = {
        name: this.clockingPlace.name,
        latitude: this.selectedLocationMarker.lat.toFixed(6),
        longitude: this.selectedLocationMarker.lng.toFixed(6),
        radius: '50',
      };
    }

    return {
      timeTrackingMethod: this.selectedTrackingMethod,
      autoApproveClockedTime: this.selectedAutoApproveHoursOption,
      autoApproveSchedule: this.selectedAutoApproveScheduleOption,
      clockLocation,
    };
  }

  // Remove when TMP_TIME_TRACKING_ENDPOINT is removed
  private saveTimeTrackingGeneralSettings() {
    let shouldUpdateSettings = false;

    const shouldChangeCopySchedule =
      (this.selectedTrackingMethod === TimeTrackingMethod.COPY_FROM_SCHEDULE &&
        !this.timeTrackingGeneralSettings.timesheet_copy_schedule) ||
      (this.selectedTrackingMethod !== TimeTrackingMethod.COPY_FROM_SCHEDULE &&
        this.timeTrackingGeneralSettings.timesheet_copy_schedule);

    if (shouldChangeCopySchedule) {
      this.timeTrackingGeneralSettings = {
        ...this.timeTrackingGeneralSettings,
        timesheet_copy_schedule: this.selectedTrackingMethod === TimeTrackingMethod.COPY_FROM_SCHEDULE,
      };
      shouldUpdateSettings = true;
    }

    if (this.selectedTrackingMethod === TimeTrackingMethod.COPY_FROM_SCHEDULE) {
      this.timeTrackingGeneralSettings = {
        ...this.timeTrackingGeneralSettings,
        approve_schedule: this.selectedAutoApproveScheduleOption === AutoApproveTimesheetHoursOption.YES,
      };
      shouldUpdateSettings = true;
    }

    if (shouldUpdateSettings) {
      return this.departmentVariationAPI.add(
        this.timeTrackingGeneralSettings,
        DEFAULT_SETTINGS_VARIATION_ID,
        DepartmentVariationSection.TIMETRACKING_GENERAL,
      );
    }

    return of(undefined);
  }

  // Remove when TMP_TIME_TRACKING_ENDPOINT is removed
  private saveTimeTrackingClockingSettings() {
    let shouldUpdateSettings = false;

    if (this.selectedTrackingMethod === TimeTrackingMethod.SELF_CLOCKING) {
      this.timeTrackingClockingSettings = {
        ...this.timeTrackingClockingSettings,
        round_clock_in: 'Nearest',
        round_clock_out: 'Nearest',
      };
      shouldUpdateSettings = true;
    }

    const shouldChangeApproveClock =
      (this.selectedAutoApproveHoursOption !== AutoApproveTimesheetHoursOption.YES &&
        this.timeTrackingClockingSettings.approve_clock !== 'Never') ||
      (this.selectedAutoApproveHoursOption === AutoApproveTimesheetHoursOption.YES &&
        this.timeTrackingClockingSettings.approve_clock !== 'RosterMatch');

    if (shouldChangeApproveClock) {
      this.timeTrackingClockingSettings = {
        ...this.timeTrackingClockingSettings,
        approve_clock:
          this.selectedAutoApproveHoursOption === AutoApproveTimesheetHoursOption.YES ? 'RosterMatch' : 'Never',
      };
      shouldUpdateSettings = true;
    }

    if (shouldUpdateSettings) {
      return this.departmentVariationAPI.add(
        this.timeTrackingClockingSettings,
        DEFAULT_SETTINGS_VARIATION_ID,
        DepartmentVariationSection.TIMETRACKING_CLOCKING,
      );
    }

    return of(undefined);
  }

  // Remove when TMP_TIME_TRACKING_ENDPOINT is removed
  private savePermissions() {
    // Add Create own timesheet permission to employees (if needed)
    if (this.selectedTrackingMethod === TimeTrackingMethod.MANUAL) {
      // Fetch permissions and permission groups in case they're not in the store yet (most likely they aren't)
      return combineLatest([this.permissionService.load(), this.permissionGroupService.load()]).pipe(
        switchMap(() =>
          combineLatest([
            this.store.select(getAccount),
            this.store.select(getPermissions),
            this.store.select(getPermissionGroups),
          ]),
        ),
        switchMap(([account, permissions, permissionGroups]) => {
          const addOwnTimesheetsPermission = permissions?.find(
            (permission) => permission.key === 'Create own timesheet',
          );
          const groupsWithAddOwnTimesheetsPermission = permissionGroups
            .filter((group) => group.Permission?.includes(addOwnTimesheetsPermission?.id))
            .map((group) => group.id);

          // Group names are localized and there isn't an easy way to identify which group is "Employee"
          // We assume the default group (Employee by default) didn't change so we use it as the Employee group
          const employeeGroupId = account.group_id;

          if (!groupsWithAddOwnTimesheetsPermission.includes(employeeGroupId)) {
            // Include Employee to the list of groups that have Create own timesheet permission
            return this.permissionService.savePermissions({
              [addOwnTimesheetsPermission.id]: [...groupsWithAddOwnTimesheetsPermission, employeeGroupId],
            });
          } else {
            return of(undefined);
          }
        }),
        take(1),
      );
    }

    return of(undefined);
  }

  // Remove when TMP_TIME_TRACKING_ENDPOINT is removed
  private getDefaultVariationSettings(section: DepartmentVariationSection) {
    return this.departmentVariationAPI.load(section).pipe(
      filter((variations) => variations?.length > 0),
      map((variations) => variations.find((variation) => variation.department_id === DEFAULT_SETTINGS_VARIATION_ID)),
      take(1),
    );
  }
}
